import React, { useState, useEffect } from 'react';
import { t } from '../../utils/i18n';

const MyAttendancePage = ({ api, user }) => {
  const [biometricDevices, setBiometricDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [punching, setPunching] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('summary'); // 'summary' or 'records'
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [stats, setStats] = useState({ totalPresent: 0, totalAbsent: 0, totalLate: 0, avgWorkHours: 0 });

  useEffect(() => {
    fetchMyAttendance();
    fetchDevices();
  }, [dateRange, view]);

  const fetchDevices = async () => {
    try {
      const resp = await api.get('/biometric_devices');
      // backend returns data.data.biometric_devices in many endpoints
      const list = (resp && resp.data && resp.data.biometric_devices) || resp.biometric_devices || resp || [];
      // only active devices
      const active = Array.isArray(list) ? list.filter(d => d.is_active) : [];
      setBiometricDevices(active);
      if (active.length > 0 && !selectedDevice) setSelectedDevice(active[0].id);
    } catch (err) {
      console.error('Failed to fetch devices', err);
      setBiometricDevices([]);
    }
  };

  const fetchMyAttendance = async () => {
    setLoading(true);
    try {
      if (view === 'summary') {
        const params = new URLSearchParams(dateRange);
        const response = await api.get(`/attendance_summaries/mine?${params}`);
        // backend may return either { attendance_summaries: [...] } or { data: { attendance_summaries: [...] } }
        const list = (response && response.attendance_summaries) || (response && response.data && response.data.attendance_summaries) || [];
        setSummaries(list);
        calculateStats(list);
      } else {
        const response = await api.get('/attendance_records');
        const list = (response && response.attendance_records) || (response && response.data && response.data.attendance_records) || [];
        setAttendanceRecords(list);
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const totalPresent = data.filter(d => d.status === 'present').length;
    const totalAbsent = data.filter(d => d.status === 'absent').length;
    const totalLate = data.filter(d => d.late_minutes > 0).length;
    const avgSeconds = data.reduce((sum, d) => sum + (d.worked_seconds || 0), 0) / (data.length || 1);
    const avgWorkHours = (avgSeconds / 3600).toFixed(1);

    setStats({ totalPresent, totalAbsent, totalLate, avgWorkHours });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handlePunch = async () => {
    if (!selectedDevice) return alert('Please select a biometric device');
    setPunching(true);
    try {
      // API expects biometric_device_id at top-level
      const { response, data } = await api.post('/attendance_records', { biometric_device_id: selectedDevice });
      if (response && response.ok) {
        alert(`Punch recorded`);
        fetchMyAttendance();
      } else {
        const errMsg = (data && data.error) || 'Failed to record punch';
        alert(errMsg);
      }
    } catch (err) {
      console.error('Punch failed', err);
      alert('Punch failed');
    } finally {
      setPunching(false);
    }
  };

  return (
    <div className="contentArea">
      <div className="dFlex baseTableHd mb_24">
        <h1 className="txt28 fwBold colorBlack">My Attendance</h1>
        <div className="dFlex" style={{ gap: '12px' }}>
          <button
            className={view === 'summary' ? 'btnAdd me-3' : 'btnSecondary'}
            onClick={() => setView('summary')}
          >
            Summary
          </button>
          <button
            className={view === 'records' ? 'btnAdd me-3' : 'btnSecondary'}
            onClick={() => setView('records')}
          >
            Records
          </button>
        </div>
      </div>

      <div className="dFlex" style={{ gap: '16px', marginBottom: '24px' }}>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Total Present
            <span className="txt30 fwBold colorBlack">{stats.totalPresent}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Total Absent
            <span className="txt30 fwBold colorBlack">{stats.totalAbsent}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Late Days
            <span className="txt30 fwBold colorBlack">{stats.totalLate}</span>
          </div>
        </div>
        <div className="dashFeaturesBox" style={{ flex: 1 }}>
          <div className="dashFeaturesTxt">
            Avg Work Hours
            <span className="txt30 fwBold colorBlack">{stats.avgWorkHours}h</span>
          </div>
        </div>
      </div>

      <div className="baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <div className="dFlex" style={{ gap: '12px' }}>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="form-control"
              style={{ width: '150px' }}
            />
            <span style={{ alignSelf: 'center' }}>to</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="form-control"
              style={{ width: '150px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="btnAdd me-3" onClick={fetchMyAttendance}>
              Apply Filter
            </button>

            <select
              className="form-control"
              style={{ width: '220px' }}
              value={selectedDevice || ''}
              onChange={(e) => setSelectedDevice(Number(e.target.value))}
            >
              <option value="">Select Biometric Device</option>
              {biometricDevices.map(d => (
                <option key={d.id} value={d.id}>{d.device_id || (`Device ${d.id}`)}</option>
              ))}
            </select>

            <button className="btnPrimary" onClick={handlePunch} disabled={punching}>
              {punching ? 'Punching...' : 'Punch'}
            </button>
          </div>
        </div>

        <div className="baseTableContainer">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : view === 'summary' ? (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Duration</th>
                  <th>Late (mins)</th>
                  <th>Early Leave (mins)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {summaries.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                      No attendance records found
                    </td>
                  </tr>
                ) : (
                  summaries.map((record) => (
                    <tr key={record.id}>
                      <td>{formatDate(record.date)}</td>
                      <td>{formatTime(record.first_check_in)}</td>
                      <td>{formatTime(record.last_check_out)}</td>
                      <td>{formatDuration(record.worked_seconds)}</td>
                      <td>
                        <span style={{ color: record.late_minutes > 0 ? '#dc3545' : '#28a745' }}>
                          {record.late_minutes || 0}
                        </span>
                      </td>
                      <td>{record.early_leave_minutes || 0}</td>
                      <td>
                        <span className={`status ${record.status === 'present' ? 'green' : (record.status === 'half_day' ? 'orange' : 'red')}`}>
                          {record.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Type</th>
                  <th>Device ID</th>
                </tr>
              </thead>
              <tbody>
                {attendanceRecords.length === 0 ? (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                      No punch records found
                    </td>
                  </tr>
                ) : (
                  attendanceRecords.map((record) => (
                    <tr key={record.id}>
                      <td>{new Date(record.timestamp).toLocaleString()}</td>
                      <td>
                        <span className={`status ${record.attendance_type === 'check_in' ? 'green' : 'blue'}`}>
                          {record.attendance_type === 'check_in' ? 'Check In' : 'Check Out'}
                        </span>
                      </td>
                      <td>{record.biometric_device_id || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAttendancePage;
